import React, { useState, useEffect, useMemo } from "react";
import IconSvg from "./IconSvg";
import "./style.css";
import { ArrowUpDown } from "lucide-react";
import { Buffer } from "buffer";

const StudentListModal = ({ isOpen, onClose, students, selectedCeeId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "emp_score",
    direction: "desc",
  });

  // Memoize the filtered students based on the search term
  const filteredStudents = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    if (!searchTerm) {
      return students;
    }
    return students.filter(
      (student) =>
        student.first_name?.toLowerCase()?.includes(lowerSearch) ||
        student.last_name?.toLowerCase()?.includes(lowerSearch)
    );
  }, [searchTerm, students]);

  // Memoize the sorted students based on the filtered list and sort config
  const sortedAndFilteredStudents = useMemo(() => {
    let sortableStudents = [...filteredStudents];
    if (sortConfig.key !== null) {
      sortableStudents.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (sortConfig.key === "first_name") {
          // For string comparison
          const nameA = `${a.first_name || ""} ${a.last_name || ""}`.trim();
          const nameB = `${b.first_name || ""} ${b.last_name || ""}`.trim();
          if (nameA < nameB) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (nameA > nameB) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        } else if (sortConfig.key === "emp_score") {
          // For numeric comparison
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        } else {
          // Default string comparison for other keys like 'top_role'
          if ((valA || "") < (valB || "")) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if ((valA || "") > (valB || "")) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        }
      });
    }
    return sortableStudents;
  }, [filteredStudents, sortConfig]);

  useEffect(() => {
    setSortConfig({ key: "emp_score", direction: "desc" });
  }, [isOpen]);

  if (!isOpen) return null;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getScoreBadgeClass = (emp_score) => {
    if (emp_score >= 70) {
      return "bg-green-100 text-green-800";
    } else if (emp_score >= 40) {
      return "bg-yellow-100 text-yellow-800";
    } else {
      return "bg-red-100 text-red-800";
    }
  };

  const handleOutsideClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };

  function encodeId(id) {
    const base64 = Buffer.from(id, "utf8").toString("base64");
    return base64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  }

  const getReportLink = (user_id) => {
    return encodeId(`${user_id}-B2B${selectedCeeId}`);
  };

  const getSortIcon = (name) => (
    <ArrowUpDown
      size={16}
      className={`sort-icon ${
        sortConfig.key === name ? sortConfig.direction : ""
      }`}
    />
  );

  return (
    <div className="modal-overlay" onClick={handleOutsideClick}>
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="modal-content">
        <div
          style={{
            padding: "1.6rem",
            borderBottom: "1px solid var(--color-gray-200)",
          }}
        >
          <div className="flex justify-between items-center">
            <h3 className="modal-header-title">Student Performance Details</h3>
            <button onClick={onClose} className="modal-close-button">
              <IconSvg path="M6 18L18 6M6 6l12 12" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-2">
            <input
              id="studentSearchInput"
              type="text"
              placeholder="Search by student name..."
              className="student-list-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-body">
          <div className="modal-table-container">
            <table className="modal-table">
              <thead className="modal-table thead">
                <tr>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => requestSort("first_name")}
                  >
                    Student Name {getSortIcon("first_name")}
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => requestSort("emp_score")}
                  >
                    Score {getSortIcon("emp_score")}
                  </th>
                  <th
                    scope="col"
                    // className="cursor-pointer"
                    // onClick={() => requestSort("top_role")}
                  >
                    Best Role Fit
                    {/* {getSortIcon("top_role")} */}
                  </th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="student-list">
                {sortedAndFilteredStudents.length > 0 ? (
                  sortedAndFilteredStudents.map((student) => (
                    <tr key={student.user_id}>
                      <td className="flex items-center">
                        <div className="student-list-item-profile">
                          {student.url ? (
                            <img
                              src={student.url}
                              alt={`${student.first_name} ${student.last_name}`}
                              className="outlier-profile-pic"
                            />
                          ) : (
                            <div className="outlier-profile-pic-placeholder">
                              {student.first_name?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        {student.first_name || ""} {student.last_name || ""}
                      </td>
                      <td>
                        <span
                          className={`score-badge ${getScoreBadgeClass(
                            student.emp_score
                          )}`}
                        >
                          {Number(student.emp_score || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td>{student.top_role || "Data Visualization"}</td>
                      <td className="text-right cursor-pointer">
                        <a
                          href={`https://www.careercarve.com/college-admin-report/${getReportLink(
                            student.user_id
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-link"
                        >
                          View Report
                        </a>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-gray-500">
                      No students found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="modal-footer-button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentListModal;
